import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';
import {Box, HStack, Link, Show, Text, VStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {chatAgentApi} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {GetConversationDetailResponse} from '@chat/shared/validations';

import {useLayoutMeasurements} from '../../hooks/useLayoutMeasurements';
import {MessageForm} from './components/MessageForm';
import {Timeline} from './components/Timeline';
import {getWhatsAppPhoneNumber} from './utils/getWhatsAppPhoneNumber';

interface CustomerChatProps {
  activeConversationId: string;
  conversationIds: string[];
  businessCaseId?: string;
  customerId?: string;
  isTyping?: boolean;
  isChattingDisabled?: boolean;
}

export function CustomerChat(props: CustomerChatProps) {
  const {
    topElementRef: timelineRef,
    bottomElementRef: messageFormRef,
    measurements,
  } = useLayoutMeasurements([props.activeConversationId]);

  const isRedesigned = isFeatureEnabled(featureFlags.REDESIGN_ACTIVITY_SIDEBAR);

  const {isLoading: isBusinessCaseLoading, refetch: refetchBusinessCase} = useGetBusinessCaseQuery(
    {
      businessCaseId: props.businessCaseId ?? '',
    },
    {skip: isNilOrEmpty(props.businessCaseId)}
  );

  const {
    data: activeConversationData,
    isLoading: isActiveConversationLoading,
    refetch: refetchActiveConversation,
  } = chatAgentApi.useGetAgentConversationQuery(
    {
      conversationId: props.activeConversationId,
    },
    {skip: isNilOrEmpty(props.activeConversationId)}
  );

  const {data: customerData, isLoading: isCustomerLoading} = useGetCustomerV2Query(
    {
      customerId: props.customerId ?? '',
    },
    {skip: isNilOrEmpty(props.customerId)}
  );

  const whatsAppNumber = getWhatsAppPhoneNumber(customerData);

  const handleOpenWhatsApp = () => {
    if (!whatsAppNumber) {
      return;
    }

    window.open(
      `https://wa.me/${whatsAppNumber}?utm_source=omnetic&utm_medium=crm&utm_campaign=whatsapp_chat&crm_contact_id=${customerData?.id}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const activeCustomerName = activeConversationData?.users.find((item) => item.type === 'PUBLIC');

  const isLoading = isBusinessCaseLoading || isActiveConversationLoading || isCustomerLoading;

  return (
    <DataStatus isLoading={isLoading}>
      <Box backgroundColor="general.white" height="100%">
        <VStack height="100%">
          <div
            ref={isRedesigned ? undefined : timelineRef}
            css={
              isRedesigned
                ? {flex: 1, overflow: 'auto', minHeight: 0}
                : {
                    height: measurements.availableHeight
                      ? `${measurements.availableHeight}px`
                      : 'auto',
                    overflow: 'auto',
                  }
            }
          >
            <Timeline
              activeConversationId={props.activeConversationId}
              conversationIds={props.conversationIds}
              businessCaseId={props.businessCaseId}
              refetchBusinessCase={refetchBusinessCase}
              onMediaClick={whatsAppNumber ? handleOpenWhatsApp : undefined}
            />
          </div>

          <div ref={isRedesigned ? undefined : messageFormRef}>
            <Box
              padding={4}
              backgroundColor="palettes.neutral.10.100"
              borderTop="1px solid"
              borderColor="palettes.neutral.40.100"
            >
              <VStack spacing={4}>
                <MessageForm
                  activeConversation={activeConversationData as GetConversationDetailResponse}
                  refetchActiveConversation={refetchActiveConversation}
                  businessCaseId={props.businessCaseId}
                  customerId={props.customerId}
                  isChattingDisabled={props.isChattingDisabled}
                  whatsAppPhoneNumber={whatsAppNumber}
                />

                <Show when={whatsAppNumber}>
                  <Link
                    leftIcon="communication/whatsapp"
                    size="xSmall"
                    isDisabled={!whatsAppNumber}
                    onClick={handleOpenWhatsApp}
                    title={i18n.t('entity.activityPanel.chat.openInWhatsapp')}
                  />
                </Show>

                <Show when={props.isTyping}>
                  <HStack spacing={1}>
                    <Text color="secondary" size="xxSmall" alternative>
                      {activeCustomerName?.displayName}
                    </Text>
                    <Text color="secondary" size="xxSmall">
                      {i18n.t('entity.chat.isTyping')}
                    </Text>
                  </HStack>
                </Show>
              </VStack>
            </Box>
          </div>
        </VStack>
      </Box>
    </DataStatus>
  );
}
